<template>
  <div class="bg-white border rounded p-3">
    <b-row class="g-3 mb-3">
      <b-col 
        v-for="(filter, index) in normalizedFilters" 
        :key="index"
        :sm="filter.colSm || '6'"
        :md="filter.colMd"
        :lg="filter.colLg || '3'">
        <app-card
          :label="$t(filter.labelKey)"
          :icon="filter.icon || 'fa-solid fa-filter'"
          :control-id="filter.type === 'datetime' ? (filter.id + '-data') : filter.id"
          :mandatory="filter.required || false"
          :autocomplete="filter.autocomplete || 'off'"
          variant="light"
          size="md">
          <template v-slot="{ controlId, mandatory, autocomplete }">
            <!-- Input texte -->
            <input 
              v-if="filter.type === 'text'"
              :id="controlId"
              :value="filter.value"
              type="text"
              class="form-control"
              :autocomplete="autocomplete"
              :placeholder="$t(filter.placeholderKey || filter.labelKey)"
              :aria-required="mandatory"
              @input="updateFilter(filter.prop, $event.target.value.trim())"
            />
            
            <!-- Select -->
            <select 
              v-else-if="filter.type === 'select'"
              :id="controlId"
              :value="filter.value"
              class="form-control"
              :autocomplete="autocomplete"
              :aria-required="mandatory"
              @change="updateFilter(filter.prop, $event.target.value)">
              <option value="">{{ $t(filter.defaultOptionKey) }}</option>
              <option 
                v-for="option in filter.options" 
                :key="option.value" 
                :value="option.value">
                {{ option.label || $t(option.labelKey) }}
              </option>
            </select>
            
            <!-- DateTime Picker -->
            <datetime-picker-custom 
              v-else-if="filter.type === 'datetime'"
              :format="filter.format || 'DD/MM/YYYY H:i:s'"
              :id="filter.id"
              customInputClass="form-control"
              :maxLength="filter.maxLength || 19"
              :placeholderValue="$t('common.datetime_picker_empty_date')"
              :is-mandatory="filter.required || false"
              :value="filter.value"
              @input="updateFilter(filter.prop, $event)"
            />
            
            <!-- Date Picker -->
            <datetime-picker-custom 
              v-else-if="filter.type === 'date'"
              :format="filter.format || 'DD/MM/YYYY'"
              :id="controlId"
              customInputClass="form-control"
              :maxLength="filter.maxLength || 10"
              :placeholderValue="$t('common.date_picker_empty_date')"
              :is-mandatory="filter.required || false"
              :value="filter.value"
              @input="updateFilter(filter.prop, $event)"
            />
            
            <!-- Switch Toggle -->
            <div v-else-if="filter.type === 'switch'" class="switch-container">
              <input 
                :id="controlId"
                type="checkbox"
                :checked="filter.value"
                @change="updateFilter(filter.prop, $event.target.checked)"
                class="switch-checkbox" />
              <label 
                :for="controlId" 
                class="switch-label">
                <span class="switch-text">{{ filter.value ? $t(filter.activeKey || 'common.active') : $t(filter.inactiveKey || 'common.inactive') }}</span>
              </label>
            </div>
            
            <!-- Multi-select Dropdown -->
            <div v-else-if="filter.type === 'multiselect'">
              <b-dropdown 
                :text="getMultiselectDisplay(filter)" 
                variant="outline-secondary"
                class="w-100"
                :id="controlId">
                <b-dropdown-form>
                  <div v-for="(option, index) in filter.options" 
                       :key="controlId + '-option-' + index"
                       class="p-2">
                    <b-form-checkbox 
                      :checked="option.selected"
                      @change="updateMultiselect(filter.prop, option, $event)">
                      {{ option.label || $t(option.labelKey) }}
                    </b-form-checkbox>
                  </div>
                </b-dropdown-form>
              </b-dropdown>
            </div>
          </template>
        </app-card>
      </b-col>
    </b-row>
    
    <!-- Boutons d'action -->
    <b-row class="gx-2">
      <b-col cols="6">
        <app-button
          :text="$t('filter.clr_fltr_field')"
          icon="fa-light fa-eraser"
          variant="secondary"
          outline
          :block="true"
          :aria-label="$t('filter.clr_fltr_field')"
          @click="clearFilters"
        />
      </b-col>
      <b-col cols="6">
        <app-button
          :text="$t('common.srch')"
          icon="fa-light fa-magnifying-glass"
          variant="primary"
          :block="true"
          :aria-label="searchAriaLabel"
          @click="applyFilters"
        />
      </b-col>
    </b-row>
  </div>
</template>

<script>
import DatetimePickerCustom from '@/components/common/dateTimePicker.vue'
import AppButton from '@/components/common/AppButton.vue'
import AppCard from '@/components/common/AppCard.vue'

export default {
  name: 'AppPanel',
  components: {
    DatetimePickerCustom,
    AppButton,
    AppCard
  },
  props: {
    filters: {
      type: Array,
      required: true,
      // Structure attendue :
      // [
      //   {
      //     id: 'filter-start-date',
      //     type: 'datetime|date|text|select',
      //     prop: 'startDate', // nom de la propriété dans filterValues
      //     labelKey: 'filter.start_date',
      //     placeholderKey: 'filter.start_date', // optionnel
      //     icon: 'fa-solid fa-calendar-days', // optionnel, défaut 'fa-solid fa-filter'
      //     required: false, // optionnel
      //     autocomplete: 'off', // optionnel, défaut 'off' - pour WCAG 2.2 AA
      //     colSm: '6', // optionnel, défaut '6'
      //     colMd: '4', // optionnel, pas de défaut
      //     colLg: '3', // optionnel, défaut '3'
      //     value: '', // valeur actuelle
      //     // Pour select uniquement :
      //     defaultOptionKey: 'filter.select_file_status',
      //     options: [
      //       { value: '1', labelKey: 'common.entity_account.file_status_1' }, // Traduction
      //       { value: 'CUSTOM', label: 'Custom Label' } // Label direct
      //     ],
      //     // Pour datetime/date uniquement :
      //     format: 'DD/MM/YYYY H:i:s',
      //     maxLength: 19,
      //     // Pour switch uniquement :
      //     activeKey: 'common.active', // optionnel
      //     inactiveKey: 'common.inactive', // optionnel
      //     // Pour multiselect uniquement :
      //     options: [
      //       { value: '1', labelKey: 'filter.option_1', selected: false },
      //       { value: '2', label: 'Option 2', selected: true }
      //     ],
      //     noneSelectedKey: 'filter.none_selected', // optionnel
      //     oneSelectedKey: 'filter.one_selected', // optionnel  
      //     manySelectedKey: 'filter.many_selected' // optionnel
      //   }
      // ]
    },
    searchAriaLabel: {
      type: String,
      default: function() {
        return this.$t('common.srch')
      }
    }
  },
  computed: {
    normalizedFilters() {
      // Intelligent large-screen layout to avoid gaps on each row for lg breakpoint
      const input = Array.isArray(this.filters) ? this.filters : []
      if (input.length === 0) return []

      // Clone to avoid mutating the prop
      const cloned = input.map(f => ({ ...f }))

      // Build rows based on lg widths
      const rows = []
      let currentRow = []
      let currentWidth = 0

      const getLg = f => {
        const val = f.colLg != null ? Number(f.colLg) : 3
        return isNaN(val) || val <= 0 ? 3 : Math.min(12, val)
      }

      // Keep track of original widths to decide which items may grow
      const originalLg = cloned.map(f => (f.colLg != null ? Number(f.colLg) : 3))
      const canGrow = originalLg.map(w => (isNaN(w) ? true : w <= 3))

      cloned.forEach((f, idx) => {
        const w = getLg(f)
        if (currentWidth + w > 12) {
          rows.push(currentRow)
          currentRow = [idx]
          currentWidth = w
        } else {
          currentRow.push(idx)
          currentWidth += w
        }
      })
      if (currentRow.length) rows.push(currentRow)

      // Adjust each row to a uniform grid (perfect 12 columns),
      // distributing columns evenly per row for consistent alignment
      rows.forEach(row => {
        if (!row || row.length === 0) return
        const count = row.length
        let base = Math.floor(12 / count)
        if (base < 2) base = 2 // avoid cells too narrow
        let remainder = 12 - base * count
        row.forEach((idx, i) => {
          const add = remainder > 0 ? 1 : 0
          cloned[idx].colLg = String(base + add)
          if (remainder > 0) remainder -= 1
        })
      })

      return cloned
    }
  },
  methods: {
    updateFilter(prop, value) {
      this.$emit('filter-changed', { prop, value })
    },
    updateMultiselect(prop, option, checked) {
      this.$emit('filter-changed', { 
        prop, 
        value: { 
          optionValue: option.value, 
          checked,
          type: 'multiselect'
        } 
      })
    },
    getMultiselectDisplay(filter) {
      const selectedOptions = filter.options.filter(opt => opt.selected)
      if (selectedOptions.length === 0) {
        return this.$t(filter.noneSelectedKey || 'filter.selected_msg_type_none')
      } else if (selectedOptions.length === 1) {
        return selectedOptions[0].label || this.$t(selectedOptions[0].labelKey)
      } else {
        const key = filter.manySelectedKey || 'filter.selected_msg_type_many'
        return this.$t(key, { nbMsgType: selectedOptions.length })
      }
    },
    clearFilters() {
      this.$emit('clear-filters')
    },
    applyFilters() {
      this.$emit('apply-filters')
    }
  }
}
</script>

<style scoped>
/* Gutter des rows pour Bootstrap 4 avec espacement vertical identique */
.g-3 {
  margin-right: -0.5rem;
  margin-left: -0.5rem;
}

/* Switch toggle moderne inspiré iOS/Material Design */
.switch-container {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.switch-checkbox {
  position: relative;
  width: 44px;
  height: 24px;
  appearance: none;
  background: #cdd1d8;
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.2s cubic-bezier(0.4, 0.0, 0.2, 1);
  outline: none;
  border: none;
}

.switch-checkbox:checked {
  background: var(--actionButtonColorBlue, #007bff);
}

.switch-checkbox:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.switch-checkbox::before {
  content: '';
  position: absolute;
  top: 2px;
  left: 2px;
  width: 20px;
  height: 20px;
  background: white;
  border-radius: 50%;
  transition: transform 0.2s cubic-bezier(0.4, 0.0, 0.2, 1);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
}

.switch-checkbox:checked::before {
  transform: translateX(20px);
}

.switch-checkbox:focus {
  box-shadow: 0 0 0 2px rgba(0, 123, 255, 0.25);
}

.switch-checkbox:hover:not(:disabled) {
  box-shadow: 0 0 8px rgba(0, 0, 0, 0.1);
}

.switch-label {
  cursor: pointer;
  margin: 0;
  display: flex;
  align-items: center;
  user-select: none;
}

.switch-text {
  font-size: 14px;
  color: var(--actionTextColorBlack, #333);
  font-weight: 400;
  transition: color 0.2s ease;
}

.g-3 > [class*="col-"] {
  padding-right: 0.5rem;
  padding-left: 0.5rem;
  padding-bottom: 1rem; /* Même espacement vertical qu'horizontal (0.5rem x 2) */
}

.gx-2 {
  margin-right: -0.25rem;
  margin-left: -0.25rem;
}

.gx-2 > [class*="col-"] {
  padding-right: 0.25rem;
  padding-left: 0.25rem;
}

</style>